import styled, { css } from 'styled-components';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import InlinePageActionButton from 'components/generic/InlineButton/InlinePageActionButton';
import { Typography } from 'components/generic/typography';
import type { TypeTextStyleDetailed } from 'types/autogen/contentful';
import { navigationLinkToPageAction } from '../../shared/ContentfulHelpers';
import {
    StyledBody as StyledBodyShared,
    StyledHeadline as StyledHeadlineShared,
    StyledTitle as StyledTitleShared
} from '../shared/RichTextStyledTypography';

type FontColorType = TypeTextStyleDetailed['fields']['fontColor'];
type TextAlignmentType = TypeTextStyleDetailed['fields']['textAlignment'];

// #region Styles
const StyledTypography = css<{
    $fontColor: FontColorType;
    $textAlignment: TextAlignmentType;
}>`
    ${({ $fontColor }) =>
        $fontColor &&
        css`
            color: ${$fontColor};
        `}
    ${({ $textAlignment }) =>
        $textAlignment &&
        css`
            display: block;
            text-align: ${$textAlignment};
        `}
`;

const StyledBody = styled(StyledBodyShared)<{
    $fontColor: FontColorType;
    $textAlignment: TextAlignmentType;
}>`
    ${StyledTypography}
`;

const StyledHeadline = styled(StyledHeadlineShared)<{
    $fontColor: FontColorType;
    $textAlignment: TextAlignmentType;
}>`
    ${StyledTypography}
`;

const StyledTitle = styled(StyledTitleShared)<{
    $fontColor: FontColorType;
    $textAlignment: TextAlignmentType;
}>`
    ${StyledTypography}
`;

const StyledInlinePageActionButton = styled(InlinePageActionButton)`
    color: inherit;
`;

// #endregion Styles

export default function TextStyle(props: TypeTextStyleDetailed) {
    const {
        fields: { text, fontColor, typography, textAlignment }
    } = props;
    const stylingProps = {
        $fontColor: fontColor,
        $textAlignment: textAlignment
    };

    if (!text) return null;

    const richTextOptions = {
        renderText: (text) => {
            return text.split('\n').reduce((children, textSegment, index) => {
                return [...children, index > 0 && <br key={index} />, textSegment];
            }, []);
        },
        renderMark: {
            [MARKS.BOLD]: (text) => <b>{text}</b>,
            [MARKS.ITALIC]: (text) => <i>{text}</i>,
            [MARKS.UNDERLINE]: (text) => <u>{text}</u>
        },
        renderNode: {
            [INLINES.ENTRY_HYPERLINK]: (node, children) => {
                const linkAction = navigationLinkToPageAction({ ...node.data.target });

                if (!linkAction) return children;

                return <StyledInlinePageActionButton action={linkAction}>{children}</StyledInlinePageActionButton>;
            },
            [BLOCKS.PARAGRAPH]: (node, children) => {
                switch (typography) {
                    case 'Body L':
                        return (
                            <StyledBody size="L" forwardedAs="span" {...stylingProps}>
                                {children}
                            </StyledBody>
                        );
                    case 'Body M':
                        return (
                            <StyledBody size="M" forwardedAs="span" {...stylingProps}>
                                {children}
                            </StyledBody>
                        );
                    case 'Body S':
                        return (
                            <StyledBody size="S" forwardedAs="span" {...stylingProps}>
                                {children}
                            </StyledBody>
                        );
                    case 'Heading 3XL':
                        return (
                            <StyledHeadline size="3XL" {...stylingProps}>
                                {children}
                            </StyledHeadline>
                        );
                    case 'Heading 2XL':
                        return (
                            <StyledHeadline size="2XL" {...stylingProps}>
                                {children}
                            </StyledHeadline>
                        );
                    case 'Heading XL':
                        return (
                            <StyledHeadline size="XL" {...stylingProps}>
                                {children}
                            </StyledHeadline>
                        );
                    case 'Heading L':
                        return (
                            <StyledHeadline size="L" {...stylingProps}>
                                {children}
                            </StyledHeadline>
                        );
                    case 'Heading M':
                        return (
                            <StyledHeadline size="M" {...stylingProps}>
                                {children}
                            </StyledHeadline>
                        );
                    case 'Heading S':
                        return (
                            <StyledHeadline size="S" {...stylingProps}>
                                {children}
                            </StyledHeadline>
                        );
                    case 'Title L':
                        return (
                            <StyledTitle size="L" {...stylingProps}>
                                {children}
                            </StyledTitle>
                        );
                    case 'Title M':
                        return (
                            <StyledTitle size="M" {...stylingProps}>
                                {children}
                            </StyledTitle>
                        );
                    case 'Title S':
                        return (
                            <StyledTitle size="S" {...stylingProps}>
                                {children}
                            </StyledTitle>
                        );
                    default:
                        return (
                            <StyledBody size="M" {...stylingProps}>
                                {children}
                            </StyledBody>
                        );
                }
            }
        }
    };

    return documentToReactComponents(text, richTextOptions);
}
